import React, { useEffect, useState, useRef } from "react";
import classNames from "classnames";
import { ChevronRightIcon } from "@tencent/tea-icons-react";
import { StyledProps } from "../_type";
import { DropdownBox } from "../dropdown";
import { useConfig } from "../_util/config-context";
import { noop } from "../_util/noop";
import { isMobile } from "../_util/is-mobile";
import { useOutsideClick } from "../_util/use-outside-click";
import { mergeRefs } from "../util";
import { Popover } from "../popover";
import { ListItemButton } from "./ListItemButton";
import { ListItemDecorator } from "./ListItemDecorator";
import { ListItemContent } from "./ListItemContent";
import { useListContext } from "./List";

export interface ListSubMenuProps
  extends StyledProps,
    React.LiHTMLAttributes<HTMLLIElement> {
  /**
   * 子菜单名
   */
  label?: React.ReactNode;

  /**
   * 菜单项
   */
  children?: React.ReactNode;

  /**
   * 弹出方向
   * @default "right"
   */
  placement?:
    | "right"
    | "left"
    | "right-start"
    | "right-end"
    | "left-start"
    | "left-end";

  /**
   * 展开子菜单触发方式
   * @default "hover"
   * @since 2.7.0
   */
  trigger?: "hover" | "click";

  /**
   * 是否处于禁用态
   * @since 2.5.0
   */
  disabled?: boolean;

  /**
   * 菜单项内容是否展示
   *
   * 默认为鼠标 Hover 触发，可通过该属性实现受控
   * @since 2.5.0
   */
  visible?: boolean;

  /**
   * 菜单项内容展示状态变化回调
   * @since 2.5.0
   */
  onVisibleChange?: (visible: boolean) => void;

  /**
   * 是否处于激活态
   * @since 2.6.0
   */
  current?: boolean;

  /**
   * 是否处于选中态
   * @since 2.6.0
   */
  selected?: boolean;
}

export const ListSubMenu = React.forwardRef(function ListSubMenu(
  {
    label,
    children,
    placement = "right-start",
    trigger = "hover",
    className,
    disabled,
    visible,
    current,
    selected,
    onVisibleChange = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onClick = noop,
    ...props
  }: ListSubMenuProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { classPrefix } = useConfig();
  const liRef = useRef<HTMLLIElement>(null);
  const enterTimerRef = useRef<any>(null);
  const leaveTimerRef = useRef<any>(null);

  const { type } = useListContext();

  const [hover, setHover] = useState<boolean>(false);
  const [subMenuVisible, setSubMenuVisible] = useState<boolean>(visible);
  const { listen } = useOutsideClick(liRef);

  useEffect(() => {
    return () => {
      clearTimeout(enterTimerRef.current);
      clearTimeout(leaveTimerRef.current);
    };
  }, []);

  if (disabled) {
    return (
      <li
        {...props}
        className={classNames(
          `${classPrefix}-list__submenu`,
          "is-disabled",
          className,
          {
            "is-current": current,
            "is-selected": selected,
          }
        )}
      >
        {type === "plain" ? (
          <>
            <ListItemButton>
              <ListItemContent>{label}</ListItemContent>
              <ChevronRightIcon />
            </ListItemButton>
          </>
        ) : (
          <>
            {label}
            <ChevronRightIcon />
          </>
        )}
      </li>
    );
  }

  const triggerProps: React.DetailedHTMLProps<
    React.LiHTMLAttributes<HTMLLIElement>,
    HTMLLIElement
  > =
    isMobile || trigger === "click"
      ? {
          onClick: event => {
            onClick(event);
            onVisibleChange(true);
            setHover(true);
          },
          onMouseEnter,
          onMouseLeave,
        }
      : {
          onClick,
          onMouseEnter: event => {
            onMouseEnter(event);
            clearTimeout(enterTimerRef.current);
            leaveTimerRef.current = setTimeout(() => {
              onVisibleChange(true);
              setHover(true);
            }, 150);
          },
          onMouseLeave: event => {
            onMouseLeave(event);
            clearTimeout(leaveTimerRef.current);
            enterTimerRef.current = setTimeout(() => {
              onVisibleChange(false);
              setHover(false);
            }, 150);
          },
        };

  if (isMobile || trigger === "click") {
    listen(() => {
      onVisibleChange(false);
      setHover(false);
    });
  }

  return (
    <Popover
      placementOffset={0}
      trigger={trigger}
      placement={placement}
      overlay={<DropdownBox>{children}</DropdownBox>}
      defaultVisible={subMenuVisible}
      onVisibleChange={v => {
        onVisibleChange(v);
        setSubMenuVisible(v);
      }}
      popupContainer={liRef.current}
    >
      <li
        ref={mergeRefs(ref, liRef)}
        {...props}
        className={classNames(`${classPrefix}-list__submenu`, className, {
          "is-hover": hover || subMenuVisible,
          "is-current": current,
          "is-selected": selected,
        })}
        {...triggerProps}
      >
        {type === "plain" ? (
          <>
            <ListItemButton>
              <ListItemContent>{label}</ListItemContent>
              <ChevronRightIcon />
            </ListItemButton>
          </>
        ) : (
          <>
            {label}
            <ChevronRightIcon />
          </>
        )}
      </li>
    </Popover>
  );
});

ListSubMenu.displayName = "ListSubMenu";
